import { AlertTriangle, CheckCircle, Edit3, Eye, EyeOff, List, X } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api-client';

interface WorkflowSuccessDialogProps {
	isOpen: boolean;
	onClose: () => void;
	workflowId: string;
	workflowName: string;
	isPublished?: boolean;
	onStatusChange?: (newStatus: 'published' | 'draft') => void;
}

interface ApiError {
	message: string | string[];
	error?: string;
	statusCode?: number;
}

export function WorkflowSuccessDialog({
	isOpen,
	onClose,
	workflowId,
	workflowName,
	isPublished = false,
	onStatusChange,
}: WorkflowSuccessDialogProps) {
	const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
	const [showError, setShowError] = useState<ApiError | null>(null);
	const [showSuccessToast, setShowSuccessToast] = useState<string | null>(null);
	const navigate = useNavigate();

	// Debug logging to see what props are being passed
	console.log('DEBUG: WorkflowSuccessDialog props:', {
		isOpen,
		workflowId,
		workflowName,
		isPublished,
	});

	if (!isOpen) return null;

	const handlePublishToggle = async () => {
		setIsUpdatingStatus(true);
		try {
			if (isPublished) {
				await api.flows.unpublishFlow(workflowId);
				onStatusChange?.('draft');
				setShowSuccessToast('Workflow unpublished successfully');
			} else {
				await api.flows.publishFlow(workflowId);
				onStatusChange?.('published');
				setShowSuccessToast('Workflow published successfully');
			}

			// Hide success toast after 3 seconds
			setTimeout(() => setShowSuccessToast(null), 3000);
		} catch (error: any) {
			console.error('Failed to update workflow status:', error);
			setShowError({
				message: error.message || 'Failed to update workflow status',
				error: error.error || 'Error',
				statusCode: error.statusCode || 500,
			});
		} finally {
			setIsUpdatingStatus(false);
		}
	};

	const handleNavigateToWorkflows = () => {
		onClose();
		navigate('/flows');
	};

	const handleStayAndEdit = () => {
		onClose();
		// User stays on current page to continue editing
	};

	const closeError = () => {
		setShowError(null);
	};

	// Handle both single message and array of messages
	const getErrorMessages = (error: ApiError): string[] => {
		return Array.isArray(error.message) ? error.message : [error.message];
	};

	return (
		<>
			{/* Main Success Dialog */}
			<div className="flex fixed inset-0 z-50 justify-center items-center bg-black bg-opacity-50 dark:bg-black dark:bg-opacity-70">
				<div className="mx-4 w-full max-w-md bg-white rounded-lg border shadow-xl dark:bg-gray-800 dark:border-gray-700">
					{/* Header */}
					<div className="flex justify-between items-center p-6 border-b border-gray-200 dark:border-gray-700">
						<div className="flex gap-3 items-center">
							<div className="flex justify-center items-center w-8 h-8 bg-green-100 rounded-full dark:bg-green-900/30">
								<CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400" />
							</div>
							<h2 className="text-lg font-semibold text-gray-900 dark:text-white">
								Workflow Saved Successfully
							</h2>
						</div>
						<button
							onClick={onClose}
							className="p-1 text-gray-400 rounded-md transition-colors hover:text-gray-600 dark:hover:text-gray-300"
							aria-label="Close dialog"
						>
							<X className="w-5 h-5" />
						</button>
					</div>

					{/* Content */}
					<div className="p-6">
						<div className="mb-6">
							<p className="mb-2 text-sm text-gray-600 dark:text-gray-400">
								<strong className="text-gray-900 dark:text-white">
									"{workflowName}"
								</strong>{' '}
								has been saved successfully.
							</p>
							<div className="flex gap-2 items-center">
								<span className="text-sm text-gray-500 dark:text-gray-400">
									Status:
								</span>
								<span
									className={`inline-flex items-center gap-1.5 px-2 py-0.5 text-xs font-medium rounded-full ${
										isPublished
											? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
											: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'
									}`}
								>
									{isPublished ? (
										<>
											<Eye className="w-3 h-3" />
											Published
										</>
									) : (
										<>
											<EyeOff className="w-3 h-3" />
											Draft
										</>
									)}
								</span>
							</div>
						</div>

						{/* Actions */}
						<div className="space-y-3">
							{/* Publish/Unpublish */}
							<button
								onClick={handlePublishToggle}
								disabled={isUpdatingStatus}
								className={`w-full flex items-center gap-3 p-3 text-left rounded-lg border transition-colors ${
									isPublished
										? 'bg-yellow-50 border-yellow-200 hover:bg-yellow-100 dark:border-yellow-800 dark:bg-yellow-900/20 dark:hover:bg-yellow-900/30'
										: 'bg-green-50 border-green-200 hover:bg-green-100 dark:border-green-800 dark:bg-green-900/20 dark:hover:bg-green-900/30'
								} disabled:opacity-50 disabled:cursor-not-allowed`}
							>
								<div
									className={`flex items-center justify-center w-8 h-8 rounded-full ${
										isPublished
											? 'bg-yellow-100 dark:bg-yellow-900/30'
											: 'bg-green-100 dark:bg-green-900/30'
									}`}
								>
									{isPublished ? (
										<EyeOff className="w-4 h-4 text-yellow-600 dark:text-yellow-400" />
									) : (
										<Eye className="w-4 h-4 text-green-600 dark:text-green-400" />
									)}
								</div>
								<div className="flex-1">
									<div
										className={`font-medium ${
											isPublished
												? 'text-yellow-900 dark:text-yellow-300'
												: 'text-green-900 dark:text-green-300'
										}`}
									>
										{isUpdatingStatus
											? 'Updating...'
											: isPublished
											? 'Unpublish Workflow'
											: 'Publish Workflow'}
									</div>
									<div
										className={`text-xs ${
											isPublished
												? 'text-yellow-700 dark:text-yellow-400'
												: 'text-green-700 dark:text-green-400'
										}`}
									>
										{isPublished
											? 'Make workflow private and stop accepting submissions'
											: 'Make workflow public and start accepting submissions'}
									</div>
								</div>
							</button>

							{/* Stay and Edit */}
							<button
								onClick={handleStayAndEdit}
								className="flex gap-3 items-center p-3 w-full text-left bg-blue-50 rounded-lg border border-blue-200 transition-colors hover:bg-blue-100 dark:border-blue-800 dark:bg-blue-900/20 dark:hover:bg-blue-900/30"
							>
								<div className="flex justify-center items-center w-8 h-8 bg-blue-100 rounded-full dark:bg-blue-900/30">
									<Edit3 className="w-4 h-4 text-blue-600 dark:text-blue-400" />
								</div>
								<div className="flex-1">
									<div className="font-medium text-blue-900 dark:text-blue-300">
										Continue Editing
									</div>
									<div className="text-xs text-blue-700 dark:text-blue-400">
										Stay on this page and make more changes to your workflow
									</div>
								</div>
							</button>

							{/* Navigate to Workflows */}
							<button
								onClick={handleNavigateToWorkflows}
								className="flex gap-3 items-center p-3 w-full text-left bg-gray-50 rounded-lg border border-gray-200 transition-colors hover:bg-gray-100 dark:border-gray-600 dark:bg-gray-700 dark:hover:bg-gray-600"
							>
								<div className="flex justify-center items-center w-8 h-8 bg-gray-100 rounded-full dark:bg-gray-600">
									<List className="w-4 h-4 text-gray-600 dark:text-gray-300" />
								</div>
								<div className="flex-1">
									<div className="font-medium text-gray-900 dark:text-gray-200">
										View All Workflows
									</div>
									<div className="text-xs text-gray-600 dark:text-gray-400">
										Go to the workflows table to manage all your workflows
									</div>
								</div>
							</button>
						</div>
					</div>

					{/* Footer */}
					<div className="px-6 pb-4 text-xs text-gray-500 dark:text-gray-400">
						You can change these settings anytime from the workflows table
					</div>
				</div>
			</div>

			{/* Success Toast */}
			{showSuccessToast && (
				<div className="fixed top-4 right-4 max-w-sm z-60">
					<div className="flex gap-3 items-center px-4 py-3 text-white bg-green-600 rounded-lg shadow-lg">
						<CheckCircle className="flex-shrink-0 w-5 h-5" />
						<span className="text-sm font-medium">{showSuccessToast}</span>
						<button
							onClick={() => setShowSuccessToast(null)}
							className="ml-2 text-green-200 hover:text-white"
						>
							<X className="w-4 h-4" />
						</button>
					</div>
				</div>
			)}

			{/* Error Dialog */}
			{showError && (
				<div className="flex fixed inset-0 justify-center items-center bg-black bg-opacity-50 z-60 dark:bg-black dark:bg-opacity-70">
					<div className="mx-4 w-full max-w-md bg-white rounded-lg border shadow-xl dark:bg-gray-800 dark:border-gray-700">
						{/* Error Header */}
						<div className="flex justify-between items-center p-6 border-b border-gray-200 dark:border-gray-700">
							<div className="flex gap-3 items-center">
								<div className="flex justify-center items-center w-8 h-8 bg-red-100 rounded-full dark:bg-red-900/30">
									<AlertTriangle className="w-4 h-4 text-red-600 dark:text-red-400" />
								</div>
								<h2 className="text-lg font-semibold text-gray-900 dark:text-white">
									{isPublished ? 'Unpublish' : 'Publish'} Failed
								</h2>
							</div>
							<button
								onClick={closeError}
								className="p-1 text-gray-400 rounded-md transition-colors hover:text-gray-600 dark:hover:text-gray-300"
								aria-label="Close dialog"
							>
								<X className="w-5 h-5" />
							</button>
						</div>

						{/* Error Content */}
						<div className="p-6">
							<div className="mb-4">
								<div className="flex gap-2 items-center mb-2">
									<span className="text-sm font-medium text-red-600 dark:text-red-400">
										{showError.statusCode === 400
											? 'Validation Error'
											: 'Server Error'}
									</span>
									{showError.statusCode && (
										<span className="px-2 py-0.5 text-xs font-mono bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded">
											{showError.statusCode}
										</span>
									)}
								</div>
								<p className="mb-4 text-sm text-gray-600 dark:text-gray-400">
									Unable to {isPublished ? 'unpublish' : 'publish'} the workflow.
									Please fix the following issues:
								</p>
							</div>

							{/* Error Messages */}
							<div className="mb-6 space-y-2">
								{getErrorMessages(showError).map((message, index) => (
									<div
										key={index}
										className="flex gap-2 items-start p-3 bg-red-50 rounded-lg border border-red-200 dark:bg-red-900/20 dark:border-red-800"
									>
										<div className="flex-shrink-0 w-1.5 h-1.5 bg-red-500 rounded-full mt-2"></div>
										<p className="text-sm leading-relaxed text-red-800 dark:text-red-200">
											{message}
										</p>
									</div>
								))}
							</div>

							{/* Actions */}
							<div className="flex gap-3 justify-end">
								<button
									onClick={closeError}
									className="px-4 py-2 text-sm font-medium text-gray-700 bg-white rounded-md border border-gray-300 transition-colors hover:bg-gray-50 dark:text-gray-300 dark:bg-gray-700 dark:border-gray-600 dark:hover:bg-gray-600"
								>
									Close
								</button>
								<button
									onClick={() => {
										closeError();
										handleStayAndEdit();
									}}
									className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md transition-colors hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600"
								>
									Fix Issues
								</button>
							</div>
						</div>
					</div>
				</div>
			)}
		</>
	);
}
